import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ── Cart store ──────────────────────────────────────────────────
// Persisted to localStorage under `cc_cart`. Holds line items plus an
// optional coupon code and dealID. Order-value maths (taxes, discount,
// grand total) is authoritative on the server (see useOrderValue); the
// local `subtotal()` is only a fallback while that request is in flight.
export const useCartStore = create(
  persist(
    (set, get) => ({
      items: [],
      coupon: '',
      dealID: null,

      // Add an item; if it already exists, bump its quantity.
      addItem: (item) =>
        set((state) => {
          const idx = state.items.findIndex((i) => i.subServiceID === item.subServiceID);
          if (idx !== -1) {
            const items = state.items.slice();
            items[idx] = { ...items[idx], quantity: items[idx].quantity + (item.quantity || 1) };
            return { items };
          }
          return { items: [...state.items, { ...item, quantity: item.quantity || 1 }] };
        }),

      removeItem: (subServiceID) =>
        set((state) => ({ items: state.items.filter((i) => i.subServiceID !== subServiceID) })),

      setQuantity: (subServiceID, qty) =>
        set((state) => {
          if (qty <= 0) return { items: state.items.filter((i) => i.subServiceID !== subServiceID) };
          return {
            items: state.items.map((i) => (i.subServiceID === subServiceID ? { ...i, quantity: qty } : i)),
          };
        }),

      incQty: (subServiceID) => {
        const item = get().items.find((i) => i.subServiceID === subServiceID);
        if (item) get().setQuantity(subServiceID, item.quantity + 1);
      },

      decQty: (subServiceID) => {
        const item = get().items.find((i) => i.subServiceID === subServiceID);
        if (item) get().setQuantity(subServiceID, item.quantity - 1);
      },

      clearCart: () => set({ items: [], coupon: '', dealID: null }),

      setCoupon: (coupon) => set({ coupon: coupon || '' }),
      setDealID: (dealID) => set({ dealID: dealID ?? null }),

      // ── Derived selectors ──
      itemCount: () => get().items.reduce((sum, i) => sum + i.quantity, 0),

      subtotal: () =>
        get().items.reduce(
          (sum, i) => sum + ((i.price || 0) + (i.cgst || 0) + (i.sgst || 0)) * i.quantity,
          0,
        ),

      // Total savings across line items that carry an `mrp` (original price)
      // higher than what's charged — e.g. items added from a package flow.
      savings: () =>
        get().items.reduce((sum, i) => {
          const mrp = i.mrp || 0;
          const price = i.price || 0;
          return sum + (mrp > price ? (mrp - price) * i.quantity : 0);
        }, 0),
    }),
    {
      name: 'cc_cart',
      // Only persist data, not the action functions.
      partialize: (state) => ({ items: state.items, coupon: state.coupon, dealID: state.dealID }),
    },
  ),
);

export default useCartStore;
